// Display helpers for audit rows. action values observed live (FACTS PACK §3d):
// admin.user_list, admin.user_view, admin.device_list, admin.user_view, device.enable,
// device.disable, device.share, account.export_request, audit.query, etc. Unknown
// actions fall through to the raw dotted string (still readable).

export type AuditAxisKey = 'target_id' | 'actor_user_id' | 'case_id';

// known action → zh label. Grouped by domain prefix for readability.
const ACTION_LABEL: Record<string, string> = {
  'admin.user_list': '管理員瀏覽使用者清單',
  'admin.user_view': '管理員檢視使用者',
  'admin.device_list': '管理員瀏覽裝置清單',
  'admin.device_view': '管理員檢視裝置',
  'admin.user_suspend': '停權使用者',
  'admin.user_unsuspend': '解除停權',
  'admin.user_ban': '封禁使用者',
  'admin.user_force_logout': '強制登出',
  'admin.impersonate': '模擬登入',
  'device.enable': '啟用裝置',
  'device.disable': '停用裝置',
  'device.report_lost': '標記裝置掛失',
  'device.share': '分享裝置',
  'device.unshare': '取消分享',
  'account.export_request': '帳號資料匯出',
  'account.delete_request': '帳號刪除申請',
  'audit.query': '稽核查詢',
  'apikey.issue': '簽發 API 金鑰',
  'apikey.revoke': '撤銷 API 金鑰',
  // 使用者處置(user.* 變體,與 admin.user_* 同義但不同 domain 前綴)
  'user.suspend': '停權使用者',
  'user.unsuspend': '解除停權',
  'user.ban': '封禁使用者',
  'user.force_logout': '強制登出',
  // 維運者角色治理(平台擁有者授予/撤銷)
  'user.role_grant': '授予角色',
  'user.role_revoke': '撤銷角色',
  // 開發者組織成員維護
  'admin.dev.org.member_add': '新增組織成員',
  'admin.dev.org.member_remove': '移除組織成員',
  'admin.dev.org.member_role_update': '調整組織成員角色',
};

export function actionLabel(action: string | undefined): string {
  if (!action) return '—';
  return ACTION_LABEL[action] ?? action;
}

/** Tone for the action pill — destructive/lifecycle actions stand out. */
export type Tone = 'good' | 'warn' | 'danger' | 'neutral' | 'info';

export function actionTone(action: string | undefined): Tone {
  if (!action) return 'neutral';
  if (/(ban|disable|report_lost|delete|revoke|member_remove)/.test(action)) return 'danger';
  if (/unsuspend/.test(action)) return 'good';
  if (/(suspend|force_logout|impersonate)/.test(action)) return 'warn';
  if (/(enable|issue|share|grant|member_add)/.test(action)) return 'good';
  if (action.startsWith('audit.')) return 'info';
  return 'neutral';
}

const TARGET_TYPE_LABEL: Record<string, string> = {
  user: '使用者',
  device: '裝置',
  audit: '稽核',
  apikey: 'API 金鑰',
};

export function targetTypeLabel(t: string | undefined): string {
  if (!t) return '—';
  return TARGET_TYPE_LABEL[t] ?? t;
}

const ROLE_LABEL: Record<string, string> = {
  admin: '管理員',
  owner: '擁有者',
  'security-officer': '資安專員',
  system: '系統',
};

export function actorRoleLabel(role: string | undefined): string {
  if (!role) return '—';
  return ROLE_LABEL[role] ?? role;
}

/**
 * target_id is prefixed live ("device:<id>", "user:<id>"). Split into a type hint +
 * bare id for compact display. If unprefixed, return the whole thing as the id.
 */
export function splitTargetId(targetId: string | undefined): { prefix: string | null; id: string } {
  if (!targetId) return { prefix: null, id: '—' };
  const idx = targetId.indexOf(':');
  if (idx <= 0) return { prefix: null, id: targetId };
  return { prefix: targetId.slice(0, idx), id: targetId.slice(idx + 1) };
}
